import { useEffect, useRef, useState } from 'react';
import { Check, Monitor, Moon, Sun } from 'lucide-react';
import { useTheme, type ThemeMode } from '@/theme/ThemeProvider';
import { AnchoredPortal, useAnchoredStyle } from './AnchoredPortal';
import { useMenuKeyboard } from './useMenuKeyboard';
import { accentIconProps } from './accentIcon';
import { registerEnglishArea } from '@/i18n/catalogue';
import { useT } from '@/i18n/LocaleProvider';
import { common } from '@featit/shared/i18n/messages/en/common';

registerEnglishArea('common', common);

const OPTIONS: { mode: ThemeMode; label: 'light' | 'dark' | 'system'; icon: typeof Sun }[] = [
  { mode: 'light', label: 'light', icon: Sun },
  { mode: 'dark', label: 'dark', icon: Moon },
  { mode: 'system', label: 'system', icon: Monitor },
];

export function ThemeToggle({
  menuPlacement = 'top',
  menuAlign = 'left',
}: {
  menuPlacement?: 'top' | 'bottom';
  menuAlign?: 'left' | 'right';
}) {
  const t = useT();
  const { mode, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Drawn at the document root (I-0401): a sidebar that scrolls would otherwise cut the menu off.
  const panelStyle = useAnchoredStyle(triggerRef, panelRef, open, {
    placement: menuPlacement,
    align: menuAlign,
  });
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeIndex = OPTIONS.findIndex((option) => option.mode === mode);
  const activeOption = OPTIONS[activeIndex] ?? OPTIONS[2];
  const ActiveIcon = activeOption.icon;

  useEffect(() => {
    if (!open) return undefined;
    const closeOnPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!containerRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', closeOnPointerDown);
    // The current mode, not the first: the menu opens on what is chosen.
    itemRefs.current[activeIndex]?.focus();
    return () => document.removeEventListener('pointerdown', closeOnPointerDown);
  }, [activeIndex, open]);
  // Arrows, Tab out and Escape (F-0533, KD-9).
  useMenuKeyboard({ open, close: () => setOpen(false), menuRef: panelRef, triggerRef });

  function selectMode(nextMode: ThemeMode): void {
    setMode(nextMode);
    setOpen(false);
    triggerRef.current?.focus();
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label={t('common.theme.choose', { mode: t(`common.theme.${activeOption.label}`) })}
        title={t('common.theme.current', { mode: t(`common.theme.${activeOption.label}`) })}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
          event.preventDefault();
          setOpen(true);
        }}
        className="grid h-8 w-8 cursor-pointer place-items-center rounded-md text-gray-500 transition-colors duration-150 hover:bg-[#D7DBDC]/50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:text-gray-400 dark:hover:bg-[#242424] dark:hover:text-gray-100"
      >
        <ActiveIcon className="h-4 w-4" />
      </button>

      {open && (
        <AnchoredPortal>
          <div
            ref={panelRef}
            role="menu"
            aria-label={t('common.theme.menu')}
            style={panelStyle}
            className="z-[60] w-36 overflow-y-auto rounded-md bg-[#F0F1F2] p-1 shadow-lg ring-1 ring-black/5 dark:bg-[#242424] dark:ring-white/10"
          >
            {OPTIONS.map((option, index) => {
              const Icon = option.icon;
              const selected = option.mode === mode;
              return (
                <button
                  key={option.mode}
                  ref={(element) => {
                    itemRefs.current[index] = element;
                  }}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selected}
                  onClick={() => selectMode(option.mode)}
                  className={`flex h-8 w-full cursor-pointer items-center gap-2 rounded px-2 text-left text-[13px] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus ${
                    selected
                      ? 'bg-[#DDE0E2] font-medium text-gray-950 dark:bg-[#303030] dark:text-white'
                      : 'text-gray-600 hover:bg-[#E4E6E8] hover:text-gray-950 dark:text-gray-300 dark:hover:bg-[#1f1f1f] dark:hover:text-white'
                  }`}
                >
                  {/* F-0579 ACC-4d: the menu walks the stops from its first item. */}
                  <Icon className="h-3.5 w-3.5 shrink-0" {...accentIconProps(index)} />
                  <span className="min-w-0 flex-1">{t(`common.theme.${option.label}`)}</span>
                  {selected && (
                    <Check
                      className="h-3.5 w-3.5 shrink-0"
                      aria-hidden="true"
                      {...accentIconProps(index)}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </AnchoredPortal>
      )}
    </div>
  );
}
